import { useMemo } from 'react';
import { parseDate, calculateEndDate } from '../utils/dateUtils';
import { formatServiceText, formatWorkOrderText, minutesToHours, normalizeProgress, getStatusDisplay, getStatusColor } from '../utils/formatUtils';
import { getProgressStatus, getWorkOrderStatus } from '../utils/statusUtils';
import { getStatusEmoji } from '../utils/formatUtils';

export const useGanttTransform = (workOrderGroups) => {
	const ganttData = useMemo(() => {
		if (!workOrderGroups || !Array.isArray(workOrderGroups)) return [];

		const tasks = [];
		let taskId = 1;

		// Calculate earliest date for each work order group
		workOrderGroups.forEach((workOrderGroup) => {
			const services = workOrderGroup.services || [];
			workOrderGroup.earliestDate = services.reduce((earliest, service) => {
				const serviceDate = parseDate(service.scheduledDate);
				if (!serviceDate) return earliest;
				return !earliest || serviceDate < earliest ? serviceDate : earliest;
			}, null);
		});

		workOrderGroups.forEach((workOrderGroup) => {
			const workOrderId = taskId++;
			const allServices = workOrderGroup.services || [];

			const earliestDate = workOrderGroup.earliestDate;
			const workOrderStartDate = parseDate(workOrderGroup.scheduledDate) || earliestDate;
			
			const latestEndDate = allServices.reduce((latest, service) => {
				const serviceEndDate = calculateEndDate(service.scheduledDate, service.estimatedMinutes);
				if (!serviceEndDate) return latest;
				return !latest || serviceEndDate > latest ? serviceEndDate : latest;
			}, workOrderStartDate);

			const workOrderProgress = normalizeProgress(workOrderGroup.percentageComplete ?? 0);
			const totalMinutes = workOrderGroup.estimatedMinutes || 0;
			const totalHours = minutesToHours(totalMinutes);
			const woInterval = workOrderGroup.interval || "";
			const workOrderStatus = getWorkOrderStatus(workOrderGroup);

			// Create work order parent task
			tasks.push({
				id: workOrderId,
				text: formatWorkOrderText(workOrderGroup),
				workOrder: workOrderGroup.workOrder,
				modelName: workOrderGroup.modelName || "",
				model: workOrderGroup.model || "",
				interval: woInterval,
				status: "", // Work orders don't have individual status emojis
				progress: workOrderProgress,
				percComplete: `${workOrderProgress}%`,
				start: workOrderStartDate || new Date(),
				end: latestEndDate || new Date(),
				estimatedMinutes: totalHours,
				type: `workorder-${workOrderStatus}`,
				statusColor: workOrderStatus,
				statusTextColor: getStatusColor(workOrderGroup.percentageOverTime || 0),
				open: true,
			});

			// Create service tasks
			allServices.forEach((service) => {
				const startDate = parseDate(service.scheduledDate) || new Date();
				const endDate = calculateEndDate(service.scheduledDate, service.estimatedMinutes) || startDate;
				const progressStatus = getProgressStatus(service);
				const progressField = service.percentageComplete ?? 0;
				const normalizedProgress = normalizeProgress(progressField);
				const serviceMinutes = service.estimatedMinutes || 0;
				const serviceHours = minutesToHours(serviceMinutes);

				tasks.push({
					id: taskId++,
					text: formatServiceText(service),
					workOrder: workOrderGroup.workOrder,
					modelName: workOrderGroup.modelName || "",
					model: workOrderGroup.model || "",
					interval: service.interval || "",
					status: getStatusDisplay(service.status || "S"),
					progress: normalizedProgress,
					percComplete: `${normalizedProgress}%`,
					start: startDate,
					end: endDate,
					estimatedMinutes: serviceHours,
					parent: workOrderId,
					type: `role-${progressStatus}`,
					statusCode: service.status || "S",
					icon: getStatusEmoji(service.status || "S"),
					role: service.role || "Role",
					statusColor: progressStatus,
					statusTextColor: getStatusColor(service.percentageOverTime || 0),
					serviceData: service
				});
			});
		});

		return tasks;
	}, [workOrderGroups]);

	const taskTypes = useMemo(() => [
		{ id: "workorder-ontrack", label: "Work Order - On Track" },
		{ id: "workorder-slight-over", label: "Work Order - ≤5% Over Time" },
		{ id: "workorder-moderate-over", label: "Work Order - 5-10% Over Time" },
		{ id: "workorder-severe-over", label: "Work Order - >10% Over Time" },
		{ id: "role-ontrack", label: "Role - On Track" },
		{ id: "role-slight-over", label: "Role - ≤5% Over Time" },
		{ id: "role-moderate-over", label: "Role - 5-10% Over Time" },
		{ id: "role-severe-over", label: "Role - >10% Over Time" },
	], []);

	return { ganttData, taskTypes };
};